/**
 * Unit conversion utilities for SixSight Cricket Analytics
 * All internal calculations use SI units (meters, m/s, degrees, kg, celsius)
 * This utility handles display conversion to user preferences
 */

import { 
  SpeedUnit, 
  DistanceUnit, 
  AngleUnit, 
  MassUnit, 
  TemperatureUnit,
  UnitPreferences,
  UNIT_DEFINITIONS 
} from '@/types/units';

// Base conversion factors (to/from SI units)
const SPEED_CONVERSIONS = {
  mps: { factor: 1, offset: 0 }, // SI base unit
  kmh: { factor: 3.6, offset: 0 }, // m/s * 3.6 = km/h
  mph: { factor: 2.237, offset: 0 }, // m/s * 2.237 = mph
};

const DISTANCE_CONVERSIONS = {
  meters: { factor: 1, offset: 0 }, // SI base unit
  feet: { factor: 3.281, offset: 0 }, // m * 3.281 = ft
  yards: { factor: 1.094, offset: 0 }, // m * 1.094 = yd
};

const ANGLE_CONVERSIONS = {
  degrees: { factor: 1, offset: 0 }, // Standard unit for cricket
  radians: { factor: Math.PI / 180, offset: 0 }, // deg * π/180 = rad
};

const MASS_CONVERSIONS = {
  kg: { factor: 1, offset: 0 }, // SI base unit
  lbs: { factor: 2.205, offset: 0 }, // kg * 2.205 = lbs
  oz: { factor: 35.274, offset: 0 }, // kg * 35.274 = oz
};

const TEMPERATURE_CONVERSIONS = {
  celsius: { factor: 1, offset: 0 }, // Standard unit
  fahrenheit: { factor: 1.8, offset: 32 }, // C * 1.8 + 32 = F
  kelvin: { factor: 1, offset: 273.15 }, // C + 273.15 = K
};

/**
 * Convert a speed value from SI (m/s) to the specified unit
 */
export function convertSpeed(valueInMps: number, toUnit: SpeedUnit): number {
  const conversion = SPEED_CONVERSIONS[toUnit];
  return valueInMps * conversion.factor + conversion.offset;
}

/**
 * Convert a speed value from the specified unit to SI (m/s)
 */
export function convertSpeedToSI(value: number, fromUnit: SpeedUnit): number {
  const conversion = SPEED_CONVERSIONS[fromUnit];
  return (value - conversion.offset) / conversion.factor;
}

/**
 * Convert a distance value from SI (meters) to the specified unit
 */
export function convertDistance(valueInMeters: number, toUnit: DistanceUnit): number {
  const conversion = DISTANCE_CONVERSIONS[toUnit];
  return valueInMeters * conversion.factor + conversion.offset;
}

/**
 * Convert a distance value from the specified unit to SI (meters)
 */
export function convertDistanceToSI(value: number, fromUnit: DistanceUnit): number {
  const conversion = DISTANCE_CONVERSIONS[fromUnit];
  return (value - conversion.offset) / conversion.factor;
}

/**
 * Convert an angle value from degrees to the specified unit
 */
export function convertAngle(valueInDegrees: number, toUnit: AngleUnit): number {
  const conversion = ANGLE_CONVERSIONS[toUnit];
  return valueInDegrees * conversion.factor + conversion.offset;
}

/**
 * Convert an angle value from the specified unit to degrees
 */
export function convertAngleToSI(value: number, fromUnit: AngleUnit): number {
  const conversion = ANGLE_CONVERSIONS[fromUnit];
  return (value - conversion.offset) / conversion.factor;
}

/**
 * Convert a mass value from SI (kg) to the specified unit
 */
export function convertMass(valueInKg: number, toUnit: MassUnit): number {
  const conversion = MASS_CONVERSIONS[toUnit];
  return valueInKg * conversion.factor + conversion.offset;
}

/**
 * Convert a mass value from the specified unit to SI (kg)
 */
export function convertMassToSI(value: number, fromUnit: MassUnit): number {
  const conversion = MASS_CONVERSIONS[fromUnit];
  return (value - conversion.offset) / conversion.factor;
}

/**
 * Convert a temperature value from Celsius to the specified unit
 */
export function convertTemperature(valueInCelsius: number, toUnit: TemperatureUnit): number {
  const conversion = TEMPERATURE_CONVERSIONS[toUnit];
  return valueInCelsius * conversion.factor + conversion.offset;
}

/**
 * Convert a temperature value from the specified unit to Celsius
 */
export function convertTemperatureToSI(value: number, fromUnit: TemperatureUnit): number {
  const conversion = TEMPERATURE_CONVERSIONS[fromUnit];
  return (value - conversion.offset) / conversion.factor;
}

/**
 * Format a numeric value with appropriate decimal places and unit symbol
 */
export function formatValue(
  value: number, 
  unit: string, 
  decimalPlaces: number = 1,
  includeUnit: boolean = true
): string {
  const formattedValue = value.toFixed(decimalPlaces);
  if (!includeUnit) return formattedValue;
  
  const unitDef = UNIT_DEFINITIONS[unit];
  return `${formattedValue}${unitDef?.symbol || unit}`;
}

/**
 * Convert and format a speed value according to user preferences
 */
export function formatSpeed(
  valueInMps: number, 
  preferences: UnitPreferences, 
  includeUnit: boolean = true
): string {
  const convertedValue = convertSpeed(valueInMps, preferences.speed);
  return formatValue(convertedValue, preferences.speed, preferences.decimalPlaces.speed, includeUnit);
}

/**
 * Convert and format a distance value according to user preferences
 */
export function formatDistance(
  valueInMeters: number, 
  preferences: UnitPreferences, 
  includeUnit: boolean = true
): string {
  const convertedValue = convertDistance(valueInMeters, preferences.distance);
  return formatValue(convertedValue, preferences.distance, preferences.decimalPlaces.distance, includeUnit);
}

/**
 * Convert and format an angle value according to user preferences
 */
export function formatAngle(
  valueInDegrees: number, 
  preferences: UnitPreferences, 
  includeUnit: boolean = true
): string {
  const convertedValue = convertAngle(valueInDegrees, preferences.angle);
  return formatValue(convertedValue, preferences.angle, preferences.decimalPlaces.angle, includeUnit);
}

/**
 * Convert and format a mass value according to user preferences
 */
export function formatMass(
  valueInKg: number, 
  preferences: UnitPreferences, 
  includeUnit: boolean = true
): string {
  const convertedValue = convertMass(valueInKg, preferences.mass);
  return formatValue(convertedValue, preferences.mass, preferences.decimalPlaces.mass, includeUnit);
}

/**
 * Convert and format a temperature value according to user preferences
 */
export function formatTemperature(
  valueInCelsius: number, 
  preferences: UnitPreferences, 
  includeUnit: boolean = true
): string {
  const convertedValue = convertTemperature(valueInCelsius, preferences.temperature);
  return formatValue(convertedValue, preferences.temperature, preferences.decimalPlaces.temperature, includeUnit);
}

/**
 * Get the unit symbol for display
 */
export function getUnitSymbol(unit: string): string {
  return UNIT_DEFINITIONS[unit]?.symbol || unit;
}

/**
 * Get the unit name for display
 */
export function getUnitName(unit: string, plural: boolean = false): string {
  const unitDef = UNIT_DEFINITIONS[unit];
  if (!unitDef) return unit;
  
  if (plural && unitDef.pluralName) {
    return unitDef.pluralName;
  }
  
  return unitDef.name;
}

/**
 * Cricket-specific formatting with context-aware precision
 */
export interface CricketMetricOptions {
  includeUnit?: boolean;
  precision?: 'low' | 'medium' | 'high';
  context?: 'dashboard' | 'analysis' | 'comparison';
}

export function formatCricketSpeed(
  valueInMps: number,
  preferences: UnitPreferences,
  options: CricketMetricOptions = {}
): string {
  const { includeUnit = true, precision = 'medium' } = options;
  
  // Adjust decimal places based on precision
  let decimalPlaces = preferences.decimalPlaces.speed;
  if (precision === 'low') decimalPlaces = Math.max(0, decimalPlaces - 1);
  if (precision === 'high') decimalPlaces = Math.min(2, decimalPlaces + 1);
  
  const convertedValue = convertSpeed(valueInMps, preferences.speed);
  return formatValue(convertedValue, preferences.speed, decimalPlaces, includeUnit);
}

export function formatCricketDistance(
  valueInMeters: number,
  preferences: UnitPreferences,
  options: CricketMetricOptions = {}
): string {
  const { includeUnit = true, precision = 'medium' } = options;
  
  // Cricket distances are typically precise for analysis
  let decimalPlaces = preferences.decimalPlaces.distance;
  if (precision === 'low') decimalPlaces = 0; // Whole numbers for overview
  if (precision === 'high') decimalPlaces = Math.min(2, decimalPlaces + 1);
  
  const convertedValue = convertDistance(valueInMeters, preferences.distance);
  return formatValue(convertedValue, preferences.distance, decimalPlaces, includeUnit);
}

export function formatCricketAngle(
  valueInDegrees: number,
  preferences: UnitPreferences,
  options: CricketMetricOptions = {}
): string {
  const { includeUnit = true, precision = 'medium' } = options;
  
  // Cricket angles are important for technique analysis
  let decimalPlaces = preferences.decimalPlaces.angle;
  if (precision === 'low') decimalPlaces = 0;
  if (precision === 'high') decimalPlaces = Math.min(2, decimalPlaces + 1);
  
  const convertedValue = convertAngle(valueInDegrees, preferences.angle);
  return formatValue(convertedValue, preferences.angle, decimalPlaces, includeUnit);
}